import type { ReactNode } from 'react';

/**
 * A glyph for every category in Explore.
 *
 * Drawn rather than fetched, and mapped by SLUG rather than stored in the
 * database. The categories are seeded rows that change about once a year;
 * putting an icon column beside them would mean a migration every time a
 * drawing is improved, and an administrator picking icons from a list nobody
 * has built. A map keyed by slug is edited in one file and deploys with the
 * page that uses it.
 *
 * The cost of that choice is exactly one failure mode — a category whose slug
 * is not here — and it is handled: `categoryIcon` falls back to a neutral tag
 * glyph, so a new category added in SQL looks plain rather than broken.
 *
 * Same language as the rest of the product: a 24 box, one stroke, currentColor,
 * 1.8 wide, round caps. The COLOUR comes from the section the grid sits in —
 * amber for Services, green for Marketplace — so twenty-eight tiles read as one
 * palette rather than twenty-eight decisions.
 */

const g = (children: ReactNode) => (
  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor"
    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>{children}</svg>
);

const ICONS: Record<string, ReactNode> = {
  /* ---- services ---------------------------------------------------------- */
  // A tap, mid-drip.
  plumbing: g(<><path d="M7 8h6a3 3 0 0 1 3 3v2" /><path d="M7 5.5v5" /><path d="M4.5 13h5" />
    <path d="M7 13v3.5" /><path d="M16 17.5c0 .8-.7 1.5-1.5 1.5S13 18.3 13 17.5c0-.9 1.5-2.5 1.5-2.5s1.5 1.6 1.5 2.5z" /></>),
  // A bolt through a socket plate.
  electrical: g(<><rect x="4" y="3.5" width="16" height="17" rx="2.5" />
    <path d="M13.2 7.5 9.5 12.8h3.2L10.8 17l4-5.4h-3.2z" /></>),
  // A spray bottle.
  cleaning: g(<><path d="M9 9h5.5a2 2 0 0 1 2 2v8a1.5 1.5 0 0 1-1.5 1.5h-6A1.5 1.5 0 0 1 7.5 19v-8a2 2 0 0 1 1.5-2z" />
    <path d="M10 9V6.5h4" /><path d="M14 4.5h3.5" /><path d="M17.5 4.5v2.5" /></>),
  // A shield.
  security: g(<path d="M12 3.5 19 6v6c0 4-3 7-7 8.5C8 19 5 16 5 12V6z" />),
  // A trowel and a brick course.
  construction: g(<><path d="M4 4.5h6l-2.5 5H6z" /><path d="M7 9.5 5.5 20" />
    <path d="M11.5 12h9" /><path d="M11.5 16h9" /><path d="M16 12v4" /></>),
  // A paint roller.
  painting: g(<><rect x="4" y="4" width="12" height="5" rx="1.5" /><path d="M16 6.5h3a1 1 0 0 1 1 1V11a1 1 0 0 1-1 1h-6" />
    <path d="M13 12v2.5" /><rect x="11" y="14.5" width="4" height="6" rx="1" /></>),
  // A handsaw.
  carpentry: g(<><path d="M4 5.5h9v5H8z" /><path d="M13 8h4a3 3 0 0 1 3 3v7" />
    <path d="M4 10.5c1 .8 2 .8 3 0s2-.8 3 0 2 .8 3 0" /></>),
  // An appliance with a dial.
  'appliance-repair': g(<><rect x="4.5" y="3.5" width="15" height="17" rx="2.5" />
    <circle cx="12" cy="13.5" r="3.5" /><path d="M8 7.5h3" /><path d="M15.5 7.5h.5" /></>),
  // A leaf.
  landscaping: g(<><path d="M5 19c0-7 4.5-12 14-12 0 8.5-4.5 12-9.5 12A4.5 4.5 0 0 1 5 19z" />
    <path d="M8.5 15.5 15 9.5" /></>),
  // A lorry.
  moving: g(<><path d="M3.5 6.5h9v9h-9z" /><path d="M12.5 10h4l3 3v2.5h-7z" />
    <circle cx="7" cy="17.5" r="1.8" /><circle cx="16.5" cy="17.5" r="1.8" /></>),
  // A table lamp.
  'interior-design': g(<><path d="M8 9.5 12 3.5l4 6z" /><path d="M12 9.5V17" />
    <path d="M8.5 20.5h7" /><path d="M12 17c-1.8 0-3 1.5-3 3.5h6c0-2-1.2-3.5-3-3.5z" /></>),
  // Clothes on a line.
  laundry: g(<><path d="M3 6.5c3-2 6 2 9 0s6-2 9 0" /><path d="M7 6.8v5.5a2 2 0 0 0 4 0" />
    <path d="M14 6.2v6a2 2 0 0 0 4 0" /></>),
  // A car under a rinse.
  'car-wash': g(<><path d="M4.5 17.5h15" /><path d="M6 17.5v-3l2-4h8l2 4v3" />
    <path d="M8 6.5v-2" /><path d="M12 5.5v-2" /><path d="M16 6.5v-2" /></>),
  // A spanner over a cog.
  mechanics: g(<><circle cx="11" cy="13" r="4" /><path d="M11 9V6.5" /><path d="M11 19.5V17" />
    <path d="M15 13h2.5" /><path d="M4.5 13H7" /><path d="M17.5 4.5 20 7l-3.5 3.5" /></>),
  // A screen with a signal.
  'it-services': g(<><rect x="3" y="4.5" width="18" height="11" rx="2" /><path d="M9 19.5h6" />
    <path d="M12 15.5v4" /><path d="M9.5 9.5a3.5 3.5 0 0 1 5 0" /><path d="M12 12h.01" /></>),
  // A megaphone.
  branding: g(<><path d="M4 10v4l11 5V5z" /><path d="M15 8.5a3.5 3.5 0 0 1 0 7" />
    <path d="M6.5 15v3.5a1.5 1.5 0 0 0 3 0V16" /></>),
  // A briefcase.
  professional: g(<><rect x="3" y="7.5" width="18" height="12" rx="2" />
    <path d="M9 7.5V6a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1.5" /><path d="M3 12.5h18" /></>),

  /* ---- marketplace ------------------------------------------------------- */
  // A sofa.
  furniture: g(<><path d="M4 11.5V9a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v2.5" />
    <path d="M3 12.5a2 2 0 0 1 4 0V16h10v-3.5a2 2 0 0 1 4 0V18H3z" /><path d="M6 18v1.5" /><path d="M18 18v1.5" /></>),
  // A fridge.
  appliances: g(<><rect x="6" y="2.5" width="12" height="19" rx="2.5" /><path d="M6 9.5h12" />
    <path d="M9 6v1.5" /><path d="M9 12v2" /></>),
  // A phone.
  electronics: g(<><rect x="6.5" y="2.5" width="11" height="19" rx="2.5" /><path d="M10.5 18.5h3" /></>),
  // A pan.
  kitchen: g(<><path d="M3.5 11h12v4a4 4 0 0 1-4 4h-4a4 4 0 0 1-4-4z" /><path d="M15.5 12.5h5" />
    <path d="M7 8.5c0-1.5 1.5-1.5 1.5-3" /><path d="M11 8.5c0-1.5 1.5-1.5 1.5-3" /></>),
  // A bed.
  'beds-bedding': g(<><path d="M3 18v-7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v7" /><path d="M3 15h18" />
    <path d="M7 9V7.5h5V9" /><path d="M3 18v1.5" /><path d="M21 18v1.5" /></>),
  // A framed picture.
  'home-decor': g(<><rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="M6.5 16.5 10 12l2.5 3 2.5-3.5 2.5 5z" /><circle cx="9" cy="9" r="1.2" /></>),
  // A watering can.
  'garden-outdoor': g(<><path d="M6 10h8v7a2.5 2.5 0 0 1-2.5 2.5h-3A2.5 2.5 0 0 1 6 17z" />
    <path d="M14 12.5 20 9" /><path d="M20 9l-1.5-2" /><path d="M8 10V8a2 2 0 0 1 4 0v2" /></>),
  // Stacked blocks.
  'building-material': g(<><rect x="3" y="13" width="8" height="6" rx="1" />
    <rect x="13" y="13" width="8" height="6" rx="1" /><rect x="8" y="6" width="8" height="6" rx="1" /></>),
  // A screwdriver and spanner.
  tools: g(<><path d="M14.5 6.5a3.5 3.5 0 0 0 4.5 4.5l-7.5 7.5a2 2 0 0 1-2.8-2.8z" />
    <path d="M6.5 4 4 6.5l2.5 2.5L9 6.5z" /><path d="M8 8.5 11 11.5" /></>),
  // A desk with a screen.
  office: g(<><rect x="6" y="3.5" width="12" height="8" rx="1.5" /><path d="M12 11.5v3" />
    <path d="M3 14.5h18" /><path d="M5.5 14.5v5" /><path d="M18.5 14.5v5" /></>),
  // A price tag, the neutral fallback.
  other: g(<><path d="M3.6 11.4V4.6a1 1 0 0 1 1-1h6.8a1 1 0 0 1 .7.3l8 8a1 1 0 0 1 0 1.4l-6.8 6.8a1 1 0 0 1-1.4 0l-8-8a1 1 0 0 1-.3-.7z" />
    <circle cx="8" cy="8" r="1.4" /></>),
};

/** The glyph for a slug, or the neutral one for a category added since. */
export function categoryIcon(slug: string): ReactNode {
  return ICONS[slug] ?? ICONS.other;
}
